import { Badge, Group, Paper, Text, Title } from '@mantine/core';
import { DataTable, type DataTableColumn } from 'mantine-datatable';
import { format, parseISO } from 'date-fns';
import { useAccountResourceOwnerLinks } from '../hooks/useAccountData.ts';
import type { ResourceOwnerLink } from '../model.ts';
import { SectionState } from './SectionState.tsx';

const TITLE_STYLE = { color: 'gray', fontWeight: 'normal' };

const DATE_TIME_FORMAT = 'yyyy/MM/dd HH:mm:ss';

const STATE_CONFIG: Record<string, { label: string; color: string }> = {
  LINKING: { label: '綁定處理中', color: 'blue' },
  LINKED: { label: '已綁定', color: 'green' },
  UNLINKED: { label: '已解綁', color: 'gray' },
  LINK_FAILED: { label: '綁定失敗', color: 'red' },
  INVALID: { label: '已失效', color: 'gray' },
};

const formatDateTime = (value: string | null | undefined) =>
  value ? format(parseISO(value), DATE_TIME_FORMAT) : '-';

const columns: DataTableColumn<ResourceOwnerLink>[] = [
  {
    title: '點數名稱',
    titleStyle: TITLE_STYLE,
    accessor: 'point.name',
    render: ({ point }) => (
      <Group gap="xs" wrap="nowrap">
        <Text size="sm">{point.name ?? '-'}</Text>
        {point.isDefaultDeduction && (
          <Badge size="lg" variant="light">
            預設折抵
          </Badge>
        )}
      </Group>
    ),
  },
  {
    title: '點數商會員 ID',
    titleStyle: TITLE_STYLE,
    accessor: 'resourceOwnerUserId',
    render: ({ resourceOwnerUserId }) => resourceOwnerUserId ?? '-',
  },
  {
    title: '折抵上限',
    titleStyle: TITLE_STYLE,
    accessor: 'point.maximumPrice',
    render: ({ point }) => point.maximumPrice ?? '-',
  },
  {
    title: '狀態',
    titleStyle: TITLE_STYLE,
    accessor: 'state',
    render: ({ state }) => {
      const config = STATE_CONFIG[state];
      return (
        <Badge variant="light" size="lg" color={config?.color ?? 'gray'}>
          {config?.label ?? state}
        </Badge>
      );
    },
  },
  {
    title: '綁定時間',
    titleStyle: TITLE_STYLE,
    accessor: 'linkDateTime',
    render: ({ linkDateTime }) => formatDateTime(linkDateTime),
  },
  {
    title: '解綁時間',
    titleStyle: TITLE_STYLE,
    accessor: 'unlinkDateTime',
    render: ({ unlinkDateTime }) => formatDateTime(unlinkDateTime),
  },
];

const PointLinkSection = ({ accountId }: { accountId: string }) => {
  const { data, error, isPending } = useAccountResourceOwnerLinks(accountId);

  return (
    <Paper radius="lg" p="xl" shadow="xs">
      <Title order={4} mb="md">
        點數綁定狀態
      </Title>
      <DataTable
        idAccessor="id"
        verticalSpacing="sm"
        records={data ?? []}
        columns={columns}
        fetching={isPending}
        minHeight={200}
        emptyState={
          error ? (
            <SectionState status="error" error={error} />
          ) : (
            <SectionState status="empty" message="沒有點數綁定資料" />
          )
        }
        customLoader={<SectionState status="loading" />}
      />
    </Paper>
  );
};

export { PointLinkSection };
